import { useEffect, useMemo, useState } from 'react';
import { IEwtSummary, ITargetItem } from '@nkr/models/index';
import { PatientType } from '@nkr/enums/index';
import { AgGridReact } from 'ag-grid-react';
import { ListType } from './list-type';
import { ColDef, GridApi, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowClassParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { Button, Label, OverlayTrigger } from 'react-bootstrap';
import { GridWrapper, IPotentialDonorTable, rowColorMap } from './styles';
import { users } from 'react-icons-kit/icomoon/users';
import { Icon } from 'react-icons-kit';
import SortableHeaderComponent from '../admin/center/list/common/sortable-header';
import PopoverTemplate from '@nkr/components/popover-template';

interface IProps {
    list: ITargetItem[],
    type: number,
    fullSize: boolean,
    showTargetColumn: boolean,
    showEwtModel: boolean,
    showFwtModel: boolean,
    suppressed: boolean,
    forceAutoHeight?: boolean,
    noRowsText?: string,
    heightOverride?: string,
    useRowClass: boolean,
    isHighDeclines: boolean,
    hidePotentialDonors?: boolean,
    onShowEwt: (summary: IEwtSummary, initialCount: any) => void,
    onShowFwt: (recipient: ITargetItem) => void,
    onGridReady: (e: GridReadyEvent) => void,
    onShowMortalityBreakdown?: (type: number, recipient: ITargetItem) => void
}

const PatientGrid = (props: IProps) => {
    const [api, setApi] = useState<GridApi>(null);

    useEffect(() => {
        if (api != null) {
            if (props.fullSize || props.list.length < 12) {
                api.setDomLayout('autoHeight');
            } else {
                api.setDomLayout('normal');
            }

            api.sizeColumnsToFit();
        }
    }, [props.fullSize]);

    useEffect(() => {
        if (api != null) {
            if (props.forceAutoHeight == true) {
                api.setDomLayout('autoHeight');
            }
        }
    }, [api, props.forceAutoHeight])

    const onGridReady = (e: GridReadyEvent) => {
        setApi(e.api);

        e.api.sizeColumnsToFit();

        props.onGridReady(e);
    }

    const onRowDataChanged = (e: RowDataChangedEvent) => {

    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();

        if (props.type == 1) {
            if (props.suppressed == false && props.fullSize == false) {
                e.api.setDomLayout('normal');
            }

            if (props.suppressed == true && props.list.length < 12 && props.fullSize == false) {
                e.api.setDomLayout('autoHeight');
            }
        }
    };

    const renderPotentialDonors = (data: ITargetItem) => {
        return (
            <PopoverTemplate dismiss={true}>
                <h4 style={{ marginTop: 0 }}>Pre-Selects</h4>

                <IPotentialDonorTable condensed bordered style={{ width: 350, marginBottom: 0 }}>
                    <thead>
                        <tr>
                            <th>Center</th>
                            <th>Alias</th>
                            <th>ABO</th>
                            <th>Age</th>
                            <th>Type</th>
                            <th>Match</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.potentialDonors.map(donor =>
                            <tr key={'pd-' + donor.id}>
                                <td>{donor.center}</td>
                                <td><a href={'/center/' + donor.centerId + '/donor/' + donor.id + '/profile'} target='_blank'>{donor.alias}</a></td>
                                <td>{donor.bloodType}</td>
                                <td>{donor.age}</td>
                                <td>{donor.type}</td>
                                <td>{donor.donorInMatch ? <Label bsStyle='hazard'>Y</Label> : <>N</>}</td>
                            </tr>
                        )}
                    </tbody>
                </IPotentialDonorTable>
            </PopoverTemplate>
        );
    };

    const fractionDigits = props.type == ListType.HardToMatch ? 2 : 0;

    const columns: ColDef[] = [
        {
            colId: 'rowNumber',
            width: 50,
            sortable: false,
            headerName: 'R#',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            colId: 'rCenter',
            field: 'recipientCenter',
            width: 120,
            headerName: 'R Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Recipient Center'>R Center</SortableHeaderComponent>,
        },
        {
            colId: 'rAlias',
            field: 'recipientAlias',
            width: 120,
            headerName: 'R Alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Recipient Alias'>R Alias</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                let potentialDonorsLink = '/center/' + data.recipientCenterId + '/recipient/' + data.recipientId + '/potential-donors';

                if (props.type == ListType.HardToMatch && props.hidePotentialDonors != true && data.potentialDonors.length > 0) {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div><a href={potentialDonorsLink} target='_blank'>{data.recipientAlias}</a></div>
                            <div className='potential-donors' title='Potential donors'>
                                <OverlayTrigger placement='right' trigger={['click', 'click']} rootClose overlay={renderPotentialDonors(data)}>
                                    <Icon icon={users} />
                                </OverlayTrigger>
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <a href={potentialDonorsLink} target='_blank'>{data.recipientAlias}</a>
                    );
                }
            }
        },
        {
            colId: 'dr',
            field: 'declineRatioCurrent',
            width: 75,
            headerName: 'DDRC',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Donor Decline Ratio Current'>DDRC</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                let donorDeclineLink = '/center/' + data.recipientCenterId + '/recipient/' + data.recipientId + '/profile/donor-declines?ddrtype=1';

                return (
                    <a href={donorDeclineLink} target='_blank'>{((data.declineRatioCurrent) * 100).toFixed(0)}</a>
                )
            }
        },
        {
            colId: 'dr',
            field: 'declineRatioHistoric',
            width: 75,
            headerName: 'DDRH',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Donor Decline Ratio Historic'>DDRH</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                let donorDeclineLink = '/center/' + data.recipientCenterId + '/recipient/' + data.recipientId + '/profile/donor-declines?ddrtype=2';

                return (
                    <a href={donorDeclineLink} target='_blank'>{((data.declineRatioHistoric)).toFixed(0)}</a>
                )
            }
        },
        {
            colId: "dr",
            field: "highestDeclineRatio",
            width: 75,
            headerName: 'DDR',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Donor Decline Ratio'>DDR</SortableHeaderComponent>,
            cellRendererFramework: ({ data }: ICellRendererParams) => {
                let { highestDeclineRatio, declineRatioHistoric, declineRatioCurrent, recipientCenterId, recipientId } = data as ITargetItem;

                let ddrType = (declineRatioHistoric > (declineRatioCurrent * 100)) ? 2 : 1;
                let donorDeclineLink = `/center/${recipientCenterId}/recipient/${recipientId}/profile/donor-declines?ddrtype=${ddrType}`;

                return (
                    <a href={donorDeclineLink} target='_blank' rel='noopener noreferrer'>
                        {(highestDeclineRatio * 100).toFixed(0)}
                    </a>
                );
            },
        },
        {
            colId: 'rb',
            field: 'recipientBloodType',
            width: 60,
            headerName: 'RB',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Recipient Blood Type'>RB</SortableHeaderComponent>,
        },
        {
            colId: 'cpra4',
            field: 'cPRA4',
            width: 75,
            headerName: 'cPRA4',
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params} title='Calculated Panel Reactive Antibody'>
                    cPRA
                </SortableHeaderComponent>
            ),
            cellRendererFramework: ({ data }: ICellRendererParams) => {
                const { cPRA4 } = data as ITargetItem;
                const isHardToMatch = props.type === ListType.HardToMatch;
                return <>{isHardToMatch ? cPRA4.toFixed(2) : Math.round(cPRA4)}</>;
            }
        },
        {
            colId: 'ra',
            field: 'rAge',
            width: 60,
            headerName: 'RA',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Recipient Age'>RA</SortableHeaderComponent>,
        },
        {
            colId: 'dt',
            field: 'dialysisMonths',
            width: 60,
            headerName: 'DT',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Dialysis Time (Months)'>DT</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                return (
                    <>{data.dialysisMonths > 0 ? data.dialysisMonths : '--'}</>
                )
            }
        },
        {
            colId: 'oneYearMortalityRisk',
            field: 'oneYearMortalityRisk',
            width: 70,
            headerName: '1-Yr MR',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='1-Year Mortality Risk'>MR</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                if (data.oneYearMortalityRisk == 0 || data.mortalityCalculations == null) {
                    return '--';
                }

                return (
                    <Button bsStyle='link' style={{ padding: 2 }} onClick={() => props.onShowMortalityBreakdown(props.type, data)}>{data.oneYearMortalityRisk}%</Button>
                )
            }
        },
        {
            colId: 'ePref',
            field: 'requireEplet',
            width: 70,
            headerName: 'E Pref',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Eplet Preference'>E Pref</SortableHeaderComponent>,
        },
        {
            colId: 'aWait',
            field: 'aWait',
            width: 60,
            headerName: 'AWT',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Wait Time'>WT</SortableHeaderComponent>,
        },
        {
            colId: 'ewt',
            field: 'ewt',
            width: 60,
            headerName: 'EWT',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Estimated Wait Time'>EWT</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                if (props.showEwtModel) {
                    return (
                        <Button bsStyle='link' style={{ padding: 2 }} onClick={() => props.onShowEwt(data.ewtSummary, data.initialCount)}>{data.ewt}</Button>
                    );
                }
                else {
                    return (<>{data.ewt}</>)
                }
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITargetItem;
                return data.ewt == '60+' ? 60 : parseInt(data.ewt);
            }
        },
        {
            colId: 'fwt',
            field: 'forecastedWaitTime',
            width: 70,
            headerName: 'FWT',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Forecasted Wait Time'>FWT</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                if (props.showFwtModel) {
                    return (
                        <Button bsStyle='link' style={{ padding: 2 }} onClick={() => props.onShowFwt(data)}>{data.forecastedWaitTime}</Button>
                    );
                }
                else {
                    return (<>{data.forecastedWaitTime}</>)
                }
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITargetItem;
                return data.forecastedWaitTime == '60+' ? 60 : parseInt(data.forecastedWaitTime);
            }
        },
        {
            colId: 'lag',
            field: 'monthsSince',
            width: 60,
            headerName: 'Lag',
            hide: props.type != ListType.HardToMatch && props.type != ListType.Vouchers,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Months Since Donor Donated'>Lag</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                return (
                    <>{data.patientTypeId == PatientType.StandardVoucherHolder || data.patientTypeId == PatientType.FamilyVoucherHolder ? data.monthsSince : '--'}</>
                );
            }
        },
        {
            colId: 'rType',
            field: 'patientType',
            width: 60,
            headerName: 'R Type',
            hide: props.type != ListType.HardToMatch,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Patient Type'>R Type</SortableHeaderComponent>
        },
        {
            colId: 'vType',
            field: 'patientType',
            width: 60,
            headerName: 'V Type',
            hide: props.type != ListType.Vouchers,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Voucher Type'>V Type</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITargetItem;

                return data.adpSwapSaver ? 'SS' : data.adpRtsf ? 'RTSF' : data.adpType;
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                let adpTypeValue = data.adpSwapSaver ?
                    <Label style={{ padding: '0.2rem 0.6rem' }}>SS</Label>
                    :
                    data.adpRtsf ?
                        <Label style={{ padding: '0.2rem 0.6rem' }}>RTSF</Label>
                        :
                        data.adpType;

                return (
                    <>{adpTypeValue}</>
                );
            }
        },
        {
            colId: 'db',
            field: 'donorBloodType',
            width: 60,
            headerName: 'DB',
            hide: props.type != ListType.PairedPatients,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Paired Donor Blood Type'>DB</SortableHeaderComponent>,
        },
        {
            colId: 'dAge',
            field: 'dAge',
            width: 60,
            headerName: 'DA',
            hide: props.type != ListType.PairedPatients,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Paired Donor Age'>DA</SortableHeaderComponent>
        },
        {
            colId: 'cpr',
            field: 'cprScore',
            width: 70,
            headerName: 'CPR',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Compatible Pair Ratio'>CPR</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                return data.cprScore >= 0
                    ? <>{(data.cprScore * 100).toFixed(0)}%</>
                    : <>--</>
            }
        },
        {
            colId: 'comp',
            field: 'isCompatiblePair',
            width: 60,
            headerName: 'Comp',
            hide: props.isHighDeclines == true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params} title='Is Compatible Pair?'>Comp</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITargetItem;

                return (
                    <>{data.isCompatiblePair ? 'Y' : 'N'}</>
                )
            }
        }
    ];

    const colDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.list, [props.list]);

    let height = props.fullSize ? null : (props.heightOverride ? props.heightOverride : '315px');

    if (props.suppressed == true) {
        if (props.list.length < 12) {
            height = null;
        }
    }

    return (
        <GridWrapper className='ag-theme-bootstrap' style={{ width: '100%', height }}>
            <AgGridReact
                defaultColDef={{
                    sortable: true
                }}
                columnDefs={colDefs}
                rowData={rowData}
                domLayout={'normal'}
                suppressContextMenu={true}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                alwaysShowVerticalScroll={true}
                noRowsOverlayComponentFramework={() => props.noRowsText ? <>{props.noRowsText}</> : <>No records found...</>}
                getRowClass={(params: RowClassParams) => {
                    let data = params.data as ITargetItem;

                    let rowClass = '';

                    if (props.type == ListType.Vouchers && props.useRowClass == true) {
                        let item = rowColorMap.find(x => data.majorSort >= x.majorSortMin && data.majorSort < x.majorSortMax);

                        if (item != null) {
                            rowClass = item.style;
                        }

                        if (data.isMatchable == false) {
                            rowClass = '';
                        }
                    }

                    if (props.type == ListType.HardToMatch && props.useRowClass == true) {
                        rowClass = data.majorSort > 0 && data.majorSort < 3 ? 'hard-to-match-not-available' : 'hard-to-match-available';

                        if (data.potentialDonors.length > 0 && data.potentialDonors.filter(x => x.donorInMatch).length == data.potentialDonors.length) {
                            rowClass = 'hard-to-match-in-match';
                        } else if (data.isMatchable == false) {
                            rowClass = ''
                        }
                    }

                    return rowClass;
                }}
                onGridReady={onGridReady}
                onRowDataChanged={onRowDataChanged}
                onSortChanged={onSortChanged}
                onGridSizeChanged={onGridSizeChanged}
            />
        </GridWrapper>
    );
}

export default PatientGrid;